import { Typography, Button, Modal, Tooltip, message } from 'antd';
import React, { useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { EMPTY_MESSAGES } from '../../../../constants';
import { useEntityData, useMutationUrn, useRefetch } from '../../../../EntityContext';
import { SidebarHeader } from '../SidebarHeader';
import { SetDomainModal } from './SetDomainModal';
import { useUnsetDomainMutation } from '../../../../../../../graphql/mutations.generated';
import { DomainLink } from '../../../../../../shared/tags/DomainLink';
import { useTranslation } from '../../../../../../../i18n/useTranslation';

const StyledButton = styled(Button)`
    display: block;
    margin-bottom: 8px;
`;

const ContentWrapper = styled.div<{ displayInline: boolean }>`
    ${(props) =>
        props.displayInline &&
        `
    display: flex;
    align-items: center;
    `}
`;

interface PropertiesProps {
    updateOnly?: boolean;
}

interface Props {
    readOnly?: boolean;
    properties?: PropertiesProps;
}

export const SidebarDomainSection = ({ readOnly, properties }: Props) => {
    const { t } = useTranslation();
    const updateOnly = properties?.updateOnly;
    const { entityData } = useEntityData();
    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = readOnly || entityData?.readOnly?.readOnly === true;
    const refetch = useRefetch();
    const urn = useMutationUrn();
    const [unsetDomainMutation] = useUnsetDomainMutation();
    const [showModal, setShowModal] = useState(false);
    const domain = entityData?.domain?.domain;

    const removeDomain = (urnToRemoveFrom) => {
        unsetDomainMutation({ variables: { entityUrn: urnToRemoveFrom } })
            .then(() => {
                message.success({ content: t('Removed Domain.'), duration: 2 });
                refetch?.();
            })
            .catch((e: unknown) => {
                message.destroy();
                if (e instanceof Error) {
                    message.error({ content: `Failed to remove domain: \n ${e.message || ''}`, duration: 3 });
                }
            });
    };

    const onRemoveDomain = (urnToRemoveFrom) => {
        Modal.confirm({
            title: t('Confirm Domain Removal'),
            content: t('Are you sure you want to remove this domain?'),
            onOk() {
                removeDomain(urnToRemoveFrom);
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    return (
        <div className="pa-4">
            <div className="sidebar-domain-section">
                <SidebarHeader title={t('Domain')} />
                <ContentWrapper displayInline={!!domain}>
                    {domain && (
                        <DomainLink
                            domain={domain}
                            closable={!isReadOnly && !updateOnly}
                            readOnly={isReadOnly}
                            onClose={(e) => {
                                e.preventDefault();
                                onRemoveDomain(entityData?.domain?.associatedUrn);
                            }}
                            fontSize={14}
                        />
                    )}
                    {(!domain || !!updateOnly) && (
                        <>
                            {!domain && (
                                <Typography.Paragraph type="secondary">
                                    {t(EMPTY_MESSAGES.domain.title)}. {t(EMPTY_MESSAGES.domain.description)}
                                </Typography.Paragraph>
                            )}
                            {!domain && (
                                <Tooltip title={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined}>
                                    <StyledButton
                                        type="default"
                                        onClick={() => setShowModal(true)}
                                        aria-label={t('Set Domain')}
                                        disabled={isReadOnly}
                                    >
                                        <EditOutlined aria-hidden="true" /> {t('Set Domain')}
                                    </StyledButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                </ContentWrapper>
                {showModal && (
                    <SetDomainModal
                        urns={[urn]}
                        refetch={refetch}
                        onCloseModal={() => {
                            setShowModal(false);
                        }}
                    />
                )}
            </div>
        </div>
    );
};
